import {
    ChangeDetectionStrategy,
    Component,
    EventEmitter,
    Input,
    OnChanges,
    OnInit,
    Output,
    SimpleChanges,
    inject,
} from '@angular/core';

import { ofType } from '@ngrx/effects';
import { ActionsSubject } from '@ngrx/store';
import { BehaviorSubject, Observable } from 'rxjs';
import { filter, map, switchMap, take, takeUntil } from 'rxjs/operators';

import {
    Action,
    FieldDefinitionOption,
    FormDataResult,
    FormDefinition,
    FormElementLayoutDefinition,
    FormFunctionResult,
    FormSectionLayoutDefinition,
    Trigger,
} from '@wdx/clmi/clmi-swagger-gen';
import { FormElementType, WdxDestroyClass } from '@wdx/shared/utils';

import {
    FormLockService,
    FormPendingChangesService,
    FormSummaryConfig,
    FormSummaryLayoutService,
    FormSummaryService,
    FormTriggersService,
    IFormDynamicData,
    IFormLayout,
    dynamicFormsActions,
} from '@wdx/shared/infrastructure/form-framework';
import * as R from 'ramda';
import { FORM_SUMMARY_CHANGE_OF_CIRCUMSTANCE } from '../../../../../constants/form-summary.constants';
import { IClmiClick } from '../../../../../interfaces/clmi-click.interface';
import { FormSummaryPendingChangesService } from '../../services/form-pending-changes/form-pending-changes.service';

@Component({
    selector: 'organism-form-summary',
    templateUrl: './organism-form-summary.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
    providers: [FormPendingChangesService],
    standalone: false,
})
export class OrganismFormSummaryComponent
    extends WdxDestroyClass
    implements OnInit, OnChanges
{
    @Input() formId: string;
    @Input() appId: string;
    @Input() caseId: string;
    @Input() customEndpoint: string;
    @Input() extraContext?: Record<string, any>;
    @Input() externalForm = false;

    // When true, an external action is in process that will effect the form data
    @Input() isPreLoading: boolean;
    @Input() entityId: string;

    @Output() checkIfFormLocked = new EventEmitter<boolean>();
    @Output() clmiOnClick = new EventEmitter<IClmiClick>();

    get summaryInfoCards$(): Observable<FieldDefinitionOption[]> {
        return this.formSummaryLayoutService.summaryInfoCards$;
    }

    data$ = new BehaviorSubject<Record<string, any>>(null);
    formDetails$ = new BehaviorSubject<FormDataResult>(null);
    layoutDefinition$ = new BehaviorSubject<FormElementLayoutDefinition>(null);
    definition$ = new BehaviorSubject<FormDefinition>(null);
    isLoading$: Observable<boolean>;
    subFormData: any;
    subFormData$ = new BehaviorSubject(null);
    FORM_SUMMARY_CHANGE_OF_CIRCUMSTANCE = FORM_SUMMARY_CHANGE_OF_CIRCUMSTANCE;

    _entityId: string;

    public formSummaryPendingChangesService = inject(
        FormSummaryPendingChangesService,
    );
    private actionsSubject$ = inject(ActionsSubject);
    private dynamicDataService = inject(IFormDynamicData);
    private formLockService = inject(FormLockService);
    private formTriggersService = inject(FormTriggersService);
    private formSummaryService = inject(FormSummaryService);
    private formSummaryLayoutService = inject(FormSummaryLayoutService);

    ngOnInit(): void {
        if (this.customEndpoint) {
            this.render(null);
        }

        if (!this.appId) {
            this.actionsSubject$
                .pipe(
                    takeUntil(this.destroyed$),
                    ofType(
                        ...dynamicFormsActions.formDataCreateAndUpdateSuccessActions,
                    ),
                    filter((action: any) => action.formId === this.formId),
                )
                .subscribe((action: any) => {
                    this.render(action.response.entityId);
                });
        }
    }

    /**
     * When 'isPreLoading' has tranisitioned from true to false, re-render
     */
    ngOnChanges(changes: SimpleChanges): void {
        if (
            (changes.isPreLoading?.previousValue &&
                !changes.isPreLoading?.currentValue) ||
            changes.entityId?.currentValue
        ) {
            this.render(this.entityId);
        }
    }

    render(entityId: string): void {
        this.data$.next(null);

        if (!this.formId) {
            return;
        }

        this.dynamicDataService
            .getFormLayoutAndDefinition(this.formId)
            .pipe(
                take(1),
                switchMap(({ definition, layoutAndDefinitions }) =>
                    this.formSummaryService
                        .getFormData(
                            this.formId,
                            entityId,
                            definition,
                            this.customEndpoint,
                            this.appId,
                            this.externalForm,
                        )
                        .pipe(
                            takeUntil(this.destroyed$),
                            filter((formData) => Boolean(formData)),
                            map((formData) =>
                                this.extraContext
                                    ? ({
                                          ...formData,
                                          context: R.mergeDeepRight(
                                              formData.context || {},
                                              this.extraContext,
                                          ),
                                      } as FormDataResult)
                                    : formData,
                            ),
                            switchMap((formData) =>
                                this.formSummaryService.getInitialisationFunctions(
                                    this.formId,
                                    definition,
                                    formData,
                                ),
                            ),
                            map(({ formData, formFunctionResults }) => {
                                formData =
                                    this.formSummaryService.applyInitialisationUpdateFields(
                                        definition.conditions,
                                        formData,
                                    );

                                const {
                                    layoutAndDefinitionsWithConditions,
                                    valueUpdates,
                                } = this.getLayoutAndDefinitionsWithConditions(
                                    definition,
                                    layoutAndDefinitions,
                                    formFunctionResults,
                                    formData,
                                );
                                formData =
                                    this.formSummaryService.applyUpdateFieldsValueChanges(
                                        valueUpdates,
                                        formData,
                                    );
                                // apply defaults last so other conditions have already completed
                                formData =
                                    this.formSummaryService.applyFieldDefaultConditions(
                                        definition?.conditions?.defaults,
                                        formData,
                                        layoutAndDefinitionsWithConditions,
                                    );
                                return {
                                    layoutAndDefinition: {
                                        sectionLayoutDefinitions:
                                            layoutAndDefinitionsWithConditions,
                                    },
                                    data: formData.data,
                                    formDetails: formData,
                                    definition: definition,
                                };
                            }),
                        ),
                ),
            )
            .subscribe(
                ({ layoutAndDefinition, data, formDetails, definition }) => {
                    this.formSummaryPendingChangesService.updatePendingData(
                        layoutAndDefinition as IFormLayout,
                        formDetails,
                    );

                    this.layoutDefinition$.next({
                        ...layoutAndDefinition,
                        name: '',
                        elementType: FormElementType.Field,
                    });
                    this.definition$.next(definition);
                    this.data$.next(data);
                    this.formDetails$.next(formDetails);
                    this.formSummaryLayoutService.getSummaryInfoCards(
                        definition,
                        data,
                    );
                    this.checkIfFormLocked.emit(
                        this.formLockService.hasFullFormLock(formDetails.lock),
                    );
                },
            );
    }

    getLayoutAndDefinitionsWithConditions(
        definition: FormDefinition,
        layoutAndDefinitions: FormSectionLayoutDefinition[],
        formFunctionResults: FormFunctionResult[],
        formData: FormDataResult,
    ): FormSummaryConfig {
        const {
            layoutAndDefinitionsWithConditions,
            subFormData,
            valueUpdates,
        } = this.formSummaryService.getLayoutAndDefinitionsWithConditions(
            definition,
            layoutAndDefinitions,
            formFunctionResults,
            formData,
            this.subFormData,
        );

        if (subFormData) {
            this.subFormData$.next(subFormData);
        }

        return {
            layoutAndDefinitionsWithConditions,
            subFormData,
            valueUpdates,
        };
    }

    onClickEvent(name: string): void {
        this.clmiOnClick.emit({ name });
    }
}
